import type { Hold } from '../../types/climb';
import { clampFraction, holdRadius } from './photo-hold-roles';

/**
 * One point of a hold's outline: `[x, y]` as fractions (0-1) of the photo's
 * natural size — the same space `Hold.x/y/width/height` live in, so a point and
 * a rectangle can be compared without converting anything.
 *
 * A scan's own detections are reported on a 0-1000 grid instead, because a
 * detector does not know the photo's pixel size. **The two spaces are
 * deliberate and are never unified**; the conversion happens in exactly one
 * place, `gym-admin/components/scans/detected-holds.ts`.
 */
export type OutlinePoint = [number, number];

/** Three points is the least that encloses anything. */
const MIN_OUTLINE_POINTS = 3;

/** As many as the server keeps (ClimbScans::DetectedHold::MAX_POINTS). */
export const MAX_OUTLINE_POINTS = 64;

/**
 * The hold's own shape, or null when it has none and is the circle its
 * rectangle describes.
 *
 * Every consumer asks through here rather than reading `hold.outline`, so a
 * shape too small to enclose anything — a climb saved by a client that got it
 * wrong — reads as "no shape" everywhere at once instead of drawing a sliver.
 */
export function holdOutline(hold: Pick<Hold, 'outline'>): OutlinePoint[] | null {
  const outline = hold.outline;
  if (!outline || outline.length < MIN_OUTLINE_POINTS) return null;

  return outline;
}

/**
 * The outline's bounding rectangle, in the convention a hold is stored in: x/y
 * the **centre**, width/height the extents, all fractions.
 *
 * The same arithmetic the server does on save (`parse_holds`), which is what
 * makes the rectangle derived rather than agreed: both sides compute it from
 * the points instead of trusting what was sent.
 */
export function outlineBox(outline: OutlinePoint[]): Pick<Hold, 'x' | 'y' | 'width' | 'height'> {
  let left = Infinity;
  let right = -Infinity;
  let top = Infinity;
  let bottom = -Infinity;

  for (const [x, y] of outline) {
    if (x < left) left = x;
    if (x > right) right = x;
    if (y < top) top = y;
    if (y > bottom) bottom = y;
  }

  return {
    x: (left + right) / 2,
    y: (top + bottom) / 2,
    width: right - left,
    height: bottom - top,
  };
}

/** A hold that is a shape: the points, and the rectangle they imply. */
export function outlinedHold(outline: OutlinePoint[], color: string): Hold {
  const points: OutlinePoint[] = outline
    .slice(0, MAX_OUTLINE_POINTS)
    .map(([x, y]) => [clampFraction(x), clampFraction(y)]);

  return { ...outlineBox(points), color, outline: points };
}

/**
 * The hold as an SVG path in the photo's natural pixels: its outline where it
 * has one, and otherwise the ellipse inscribed in its rectangle — which is the
 * circle every hold drawn by hand is.
 *
 * One helper for both so a renderer draws `<path d={holdPath(...)} />` without
 * asking which kind of hold it has. Its length is normalised by `pathLength`
 * where it is animated, so the draw-on sweep traces a polygon as happily as a
 * circle.
 */
export function holdPath(hold: Hold, naturalWidth: number, naturalHeight: number): string {
  const outline = holdOutline(hold);
  if (outline) {
    const [first, ...rest] = outline.map(
      ([x, y]) => `${round(x * naturalWidth)} ${round(y * naturalHeight)}`
    );

    return `M ${first} ${rest.map((point) => `L ${point}`).join(' ')} Z`;
  }

  const cx = hold.x * naturalWidth;
  const cy = hold.y * naturalHeight;
  const rx = (hold.width / 2) * naturalWidth;
  const ry = (hold.height / 2) * naturalHeight;

  // Two half-arcs rather than one: a single arc back to where it started draws
  // nothing at all, which is what the spec says of a zero-length sweep.
  return [
    `M ${round(cx - rx)} ${round(cy)}`,
    `A ${round(rx)} ${round(ry)} 0 0 1 ${round(cx + rx)} ${round(cy)}`,
    `A ${round(rx)} ${round(ry)} 0 0 1 ${round(cx - rx)} ${round(cy)}`,
    'Z',
  ].join(' ');
}

/**
 * How thick to draw a mark's line, in the units of whatever box it is being
 * drawn in.
 *
 * **Two rules, because there are two kinds of mark and they want opposite
 * things.**
 *
 * A circle drawn by hand is sized by the person drawing it, so its ring is a
 * share of the picture — the rule every climb on the app has been drawn with,
 * left exactly as it was so nothing already saved changes.
 *
 * A **traced outline** is not: it is as big as the hold is, and a hold on a
 * 4032 px wall is about 70 px across. The same share of the picture puts a
 * 20 px line on it — a third as thick as the shape it is drawing — which is
 * what made a measured outline read as a coarse blob. So a shape's line is a
 * share of the *hold's* short side instead, clamped against the picture at
 * both ends so a volume gets no band and a foot chip no hairline.
 */
const SHAPE_STROKE_SHARE = 0.06;
const SHAPE_STROKE_MIN = 0.0012;
const SHAPE_STROKE_MAX = 0.004;
const CIRCLE_STROKE_SHARE = 0.005;

export function holdStrokeWidth(
  hold: Hold,
  naturalWidth: number,
  naturalHeight: number,
  referenceEdge: number
): number {
  if (!holdOutline(hold)) return referenceEdge * CIRCLE_STROKE_SHARE;

  const short = Math.min(hold.width * naturalWidth, hold.height * naturalHeight);

  return Math.min(
    Math.max(short * SHAPE_STROKE_SHARE, referenceEdge * SHAPE_STROKE_MIN),
    referenceEdge * SHAPE_STROKE_MAX
  );
}

/**
 * The shape as a CSS `clip-path`, in percentages of the hold's **own box** —
 * for the editor, which draws each mark as an absolutely positioned element
 * sized to that box.
 *
 * It is what makes the tap target the real shape: the browser honours a
 * clip-path when hit-testing, exactly as it honours the `border-radius` that
 * does this job for a circle. Undefined for a hold with no outline, which is
 * already round.
 */
export function holdClipPath(hold: Hold): string | undefined {
  const outline = holdOutline(hold);

  return outline ? outlineClipPath(outline) : undefined;
}

/**
 * The same thing for a shape that is not a hold yet — a scan's detection, whose
 * points are on the 0-1000 grid.
 *
 * Percentages of the shape's own box are scale-free, so this works in either
 * coordinate space without being told which one it was given. Undefined for a
 * shape with no area, which has no inside to clip to.
 */
export function outlineClipPath(outline: OutlinePoint[]): string | undefined {
  const box = outlineBox(outline);
  if (box.width <= 0 || box.height <= 0) return undefined;

  const left = box.x - box.width / 2;
  const top = box.y - box.height / 2;
  const points = outline.map(([x, y]) => {
    const px = ((x - left) / box.width) * 100;
    const py = ((y - top) / box.height) * 100;

    return `${round(px)}% ${round(py)}%`;
  });

  return `polygon(${points.join(', ')})`;
}

/**
 * Whether the point is inside the shape — ray casting, with the bounding box
 * asked first because most of the holds on a wall are nowhere near the finger.
 *
 * A point exactly on an edge is not promised either answer; a press is a blunt
 * instrument and the tie goes to whichever way the arithmetic falls.
 */
export function pointInOutline(point: { x: number; y: number }, outline: OutlinePoint[]): boolean {
  const box = outlineBox(outline);
  if (
    Math.abs(point.x - box.x) > box.width / 2 ||
    Math.abs(point.y - box.y) > box.height / 2
  ) {
    return false;
  }

  let inside = false;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const [xi, yi] = outline[i];
    const [xj, yj] = outline[j];
    const straddles = yi > point.y !== yj > point.y;
    if (straddles && point.x < ((xj - xi) * (point.y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }

  return inside;
}

/**
 * How much of the photo the hold covers — the shoelace area of its outline, or
 * the area of the circle it is.
 *
 * What decides which hold of a stack a press means: the smallest wins, so a
 * hold drawn inside a bigger one can still be picked up.
 */
export function holdArea(hold: Hold): number {
  const outline = holdOutline(hold);
  if (!outline) {
    const radius = holdRadius(hold);

    return Math.PI * radius * radius;
  }

  let total = 0;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    total += outline[j][0] * outline[i][1] - outline[i][0] * outline[j][1];
  }

  return Math.abs(total / 2);
}

/**
 * The shape grown by `pad` on each side, about the middle of its own box.
 *
 * What gives a press the same slack on a shape that it has on a circle: the
 * editor accepts a press a few screen pixels outside a mark, because a finger
 * is wider than a crimp. For a rectangle this is exactly the box grown by the
 * padding, which is what a circle has always had.
 */
export function grownOutline(
  outline: OutlinePoint[],
  pad: { x: number; y: number }
): OutlinePoint[] {
  const box = outlineBox(outline);
  const halfWidth = box.width / 2;
  const halfHeight = box.height / 2;
  // A shape with no extent in one axis has no middle to grow away from there.
  const sx = halfWidth > 0 ? (halfWidth + pad.x) / halfWidth : 1;
  const sy = halfHeight > 0 ? (halfHeight + pad.y) / halfHeight : 1;

  return outline.map(([x, y]) => [box.x + (x - box.x) * sx, box.y + (y - box.y) * sy]);
}

/** The shape with every point put through a projection — a crop, say. */
export function mapOutline(
  outline: OutlinePoint[],
  project: (point: OutlinePoint) => OutlinePoint
): OutlinePoint[] {
  return outline.map(project);
}

/**
 * Whether these four points are an axis-aligned rectangle — a box, which says
 * where a hold is without saying anything about its shape.
 *
 * Read by the one place that turns detections into marks: a box goes on being
 * drawn as the circle it has always been drawn as, and only a real outline is
 * carried onto the hold as one. See `detected-holds.ts`.
 */
export function isRectangle(outline: OutlinePoint[]): boolean {
  if (outline.length !== 4) return false;

  const xs = new Set(outline.map(([x]) => x));
  const ys = new Set(outline.map(([, y]) => y));

  return xs.size === 2 && ys.size === 2;
}

/** To a thousandth of a unit — plenty on a photo, and a stable string. */
function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
